import { useNavigate } from 'react-router-dom';
import { Flame, ArrowRight, MessageCircle } from 'lucide-react';
import MiniSpectrum from './MiniSpectrum';

const CATEGORY_STYLES = {
  'AI & Labor': { color: '#1d4ed8', bg: '#dbeafe' },
  'Immigration Policy': { color: '#b45309', bg: '#fef3c7' },
  Education: { color: '#059669', bg: '#d1fae5' },
  'Climate & Energy': { color: '#0891b2', bg: '#cffafe' },
  Politics: { color: '#7c3aed', bg: '#ede9fe' },
  Economy: { color: '#dc2626', bg: '#fef2f2' },
  Technology: { color: '#2563eb', bg: '#dbeafe' },
  Health: { color: '#059669', bg: '#d1fae5' }
};

const GRADIENT_POOL = [
  'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #0ea5e9 100%)',
  'linear-gradient(135deg, #7c2d12 0%, #c2410c 40%, #fb923c 100%)',
  'linear-gradient(135deg, #14532d 0%, #15803d 40%, #4ade80 100%)',
  'linear-gradient(135deg, #164e63 0%, #0e7490 40%, #22d3ee 100%)',
  'linear-gradient(135deg, #312e81 0%, #4338ca 40%, #818cf8 100%)',
  'linear-gradient(135deg, #581c87 0%, #7e22ce 40%, #c084fc 100%)'
];

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

const LeafIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" />
  </svg>
);

const EventCard = ({ event, featured = false }) => {
  const navigate = useNavigate();
  const perspectives = event.perspectives || [];
  const aiCount = perspectives.length;
  const communityCount = event.communityResponseCount || 0;
  const catStyle = CATEGORY_STYLES[event.category] || { color: '#64748b', bg: '#f1f5f9' };
  const gradient =
    GRADIENT_POOL[Math.abs(hashCode(event.slug || event.title || '')) % GRADIENT_POOL.length];
  const timeAgo = getTimeAgo(event.publishedAt);
  const isNew = timeAgo.includes('hours') || timeAgo === 'just now';

  return (
    <div
      className="bg-white rounded-2xl border border-stone-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg"
      style={{ boxShadow: featured ? '0 4px 20px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.04)' }}
      onClick={() => navigate(`/perspectives/${event.slug}`)}
    >
      {/* Hero gradient */}
      <div
        className="relative flex items-center justify-center"
        style={{
          height: featured ? '120px' : '80px',
          background: event.coverImageUrl ? `url(${event.coverImageUrl}) center/cover` : gradient
        }}
      >
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span
            className="text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded"
            style={{
              background: catStyle.bg,
              color: catStyle.color,
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {event.category}
          </span>
          {featured && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-50 text-red-600 flex items-center gap-1"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <Flame size={10} /> Trending
            </span>
          )}
          {isNew && !featured && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded bg-green-50 text-green-600"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              New
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span
            className="text-[11px] text-white/70"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {timeAgo}
          </span>
        </div>
      </div>

      <div className={featured ? 'px-6 py-5' : 'px-5 py-4'}>
        <h3
          className="font-extrabold text-slate-900 leading-tight mb-1.5"
          style={{
            fontSize: featured ? '20px' : '17px',
            fontFamily: "'Source Serif 4', Georgia, serif"
          }}
        >
          {event.title}
        </h3>
        <p
          className="text-sm text-slate-500 leading-relaxed mb-4"
          style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
        >
          {event.summary}
        </p>

        <MiniSpectrum
          perspectives={perspectives}
          spectrumLeft={event.spectrumLeftLabel}
          spectrumRight={event.spectrumRightLabel}
        />

        {/* Stats bar */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <span
              className="text-xs text-slate-500 flex items-center gap-1"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-slate-900 inline-block" />
              {aiCount} AI
            </span>
            {communityCount > 0 && (
              <span
                className="text-xs text-slate-500 flex items-center gap-1"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-600 inline-block" />
                {communityCount} community
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {(event.totalTwigs || 0) > 0 && (
              <span
                className="flex items-center gap-1 text-xs text-green-600 font-semibold"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <LeafIcon size={14} /> {(event.totalTwigs || 0).toLocaleString()}
              </span>
            )}
            {(event.totalComments || 0) > 0 && (
              <span
                className="flex items-center gap-1 text-xs text-slate-400"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <MessageCircle size={14} /> {event.totalComments}
              </span>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-3.5">
          <button
            className="w-full py-2.5 px-4 rounded-xl bg-growth-green text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:opacity-90 transition"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/perspectives/${event.slug}`);
            }}
          >
            Read Perspectives <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

export default EventCard;
